import {
  EducationalStage,
  Gender,
  getPriceFromCentimes,
  PaginatedAnnounceSearchResult,
  PartOfTheDay,
  SearchAnnouncePaginatedQueryDto,
  SearchResult,
  SortCriteria,
} from '@les-sherpas/sherpas-toolbox';
import axios from 'axios';
import server from 'config';
import { ParsedUrlQuery } from 'querystring';

import { defaultFilters } from '@/components/Search/components/Filters/Filters.stub';
import {
  indifferentLocationOfCourse,
  SidePanelFilters,
} from '@/components/Search/types';

export const ASCENDING_PRICE = 'ascendingPrice';

export const sortCriteria = [
  SortCriteria.Price,
  ASCENDING_PRICE,
  SortCriteria.Rating,
  SortCriteria.Score,
  SortCriteria.ReviewsCount,
];

export const sortCriteriaDefaultValue = 3;

const baseFilters = {
  prices: [15, 60],
  courseLocation: 'indifferent',
  gender: 'other',
  educationalStages: [],
  availabilities: [],
  onlyAnswerFastSherpas: false,
  onlyCertifiedSherpas: true,
  onlyAvailableSherpas: false,
};

export const getInitialState = (query: ParsedUrlQuery): SidePanelFilters => {
  const minPrice =
    query.min && !Number.isNaN(Number(query.min))
      ? Number(query.min)
      : getPriceFromCentimes(defaultFilters.filters.minPrice);
  const maxPrice =
    query.max && !Number.isNaN(Number(query.max))
      ? Number(query.max)
      : getPriceFromCentimes(defaultFilters.filters.maxPrice);
  const courseLocation =
    (query.courseLocation as SidePanelFilters['courseLocation']) ??
    indifferentLocationOfCourse;
  const gender = query.gender ? (query.gender as Gender) : Gender.Other;
  const educationalStages = query.educationalStages
    ? Object.values(EducationalStage).filter((l) =>
        query.educationalStages.includes(l)
      )
    : [];
  const availabilities = query.availabilities
    ? Object.values(PartOfTheDay).filter((l) =>
        query.availabilities.includes(l)
      )
    : [];
  return {
    prices: [minPrice, maxPrice],
    courseLocation,
    gender,
    educationalStages,
    availabilities,
    onlyAnswerFastSherpas: query.answerFastSherpas === 'true',
    onlyCertifiedSherpas: true,
    onlyAvailableSherpas: query.availableSherpas === 'true',
  };
};

export const getCountActiveFilters = (selector: SidePanelFilters) => {
  let activeFilterCount = 0;

  Object.entries(baseFilters).forEach(([key, defaultValue]) => {
    const currentValue = selector[key];

    const isDifferent = Array.isArray(defaultValue)
      ? defaultValue.length !== currentValue.length ||
        !defaultValue.every((val, index) => val === currentValue[index])
      : defaultValue !== currentValue;

    if (isDifferent) {
      activeFilterCount += 1;
    }
  });

  return activeFilterCount;
};

export const getSearchResult = async (
  searchQueryInput: SearchAnnouncePaginatedQueryDto
) => {
  const response = await axios.post<PaginatedAnnounceSearchResult>(
    `${server}announce/search`,
    searchQueryInput,
    {
      withCredentials: true,
      validateStatus: (status) => status === 200,
    }
  );
  return response.data;
};

export const calculateNewUrlWithCriteria = ({
  url,
  queryParams,
}: {
  url: URL;
  queryParams: Record<string, string | string[] | boolean | number>;
}): URL => {
  Object.entries(queryParams).forEach(([key, value]) => {
    url.searchParams.delete(key);

    const isNotEmptyArray = Array.isArray(value) && value.length === 0;
    const filterCriteriaValueNotChanged = defaultFilters.filters[key] === value;
    const isDefaultSort = key === 'sort' && value === sortCriteriaDefaultValue;
    const shouldRecalculate =
      !isNotEmptyArray && !filterCriteriaValueNotChanged && !isDefaultSort;

    if (shouldRecalculate) {
      const formattedValue = Array.isArray(value)
        ? value.join(',')
        : String(value);
      url.searchParams.set(key, formattedValue);
    }
  });

  return url;
};

export const getAnnounceLink = (seoId: SearchResult['seoId']): string => {
  const href = `/t/${encodeURIComponent(seoId)}`;
  return href;
};
